import { countries, CountryKey } from '../../../constants/countries';

/**
 * The charity countries an event PlaceHub program offers its fundraising
 * guarantee and registration fee to. Mirrors the API rule: the program switch
 * is on and the charity's country is on that feature's list.
 */

export const programFeature = {
  fundraisingGuarantee: 'donationPlan',
  registrationFee: 'registrationFee'
} as const;
export type ProgramFeature =
  (typeof programFeature)[keyof typeof programFeature];

export interface ProgramFeatureSettings {
  enabled?: boolean | null;
  countries?: string[] | null;
}

export type ProgramWithFeatures = Partial<
  Record<ProgramFeature, ProgramFeatureSettings | null>
>;

/** A country of the program's charities, as the API reports it. */
export interface CharityCountry {
  country: string;
  charityCount: number;
  donationPlanEnabledCount: number;
  registrationFeeEnabledCount: number;
}

/** The guarantee cannot operate in the United Kingdom. */
export const guaranteeBlockedCountry = 'GB';

export const normaliseCountry = (country?: string | null): string | null => {
  const code = country?.trim().toUpperCase();
  return code || null;
};

export const isFeatureOffered = (
  program: ProgramWithFeatures | null | undefined,
  feature: ProgramFeature,
  organizationCountry?: string | null
): boolean => {
  const settings = program?.[feature];
  const code = normaliseCountry(organizationCountry);
  if (!settings?.enabled || !code) {
    return false;
  }
  return (settings.countries ?? []).some(
    (country) => normaliseCountry(country) === code
  );
};

export const isCountryAllowed = (
  feature: ProgramFeature,
  country?: string | null
): boolean =>
  !(
    feature === programFeature.fundraisingGuarantee &&
    normaliseCountry(country) === guaranteeBlockedCountry
  );

export const getCountryLabel = (country: string): string =>
  countries[country as CountryKey]?.label ?? country;

export const getFeatureEnabledCount = (
  charityCountry: CharityCountry | undefined,
  feature: ProgramFeature
): number => {
  if (!charityCountry) {
    return 0;
  }
  return feature === programFeature.fundraisingGuarantee
    ? charityCountry.donationPlanEnabledCount
    : charityCountry.registrationFeeEnabledCount;
};

const toCodes = (list: unknown): string[] =>
  Array.isArray(list)
    ? Array.from(
        new Set(
          list
            .map((country) =>
              normaliseCountry(typeof country === 'string' ? country : null)
            )
            .filter((code): code is string => !!code)
        )
      )
    : [];

/**
 * The countries the picker offers: those of the program's charities plus any
 * on the list now or as saved, ordered by name.
 */
export const getCountryOptions = (
  charityCountries: CharityCountry[] | null | undefined,
  selected: unknown,
  saved?: unknown
): CharityCountry[] => {
  const byCode = new Map<string, CharityCountry>();
  (charityCountries ?? []).forEach((row) => {
    const code = normaliseCountry(row.country);
    if (code) {
      byCode.set(code, { ...row, country: code });
    }
  });
  [...toCodes(selected), ...toCodes(saved)].forEach((code) => {
    if (!byCode.has(code)) {
      byCode.set(code, {
        country: code,
        charityCount: 0,
        donationPlanEnabledCount: 0,
        registrationFeeEnabledCount: 0
      });
    }
  });
  return Array.from(byCode.values()).sort((a, b) =>
    getCountryLabel(a.country).localeCompare(getCountryLabel(b.country))
  );
};

export const toggleCountry = (
  selected: unknown,
  country: string,
  checked: boolean
): string[] => {
  const code = normaliseCountry(country);
  const list = toCodes(selected).filter((item) => item !== code);
  return checked && code ? [...list, code] : list;
};

/**
 * Saved countries now off the list whose charities have the feature switched
 * on, with how many: those charities stop seeing it once the program is saved.
 */
export const getRemovedCountriesWithCharities = (
  saved: unknown,
  selected: unknown,
  charityCountries: CharityCountry[] | null | undefined,
  feature: ProgramFeature
): { country: string; count: number }[] => {
  const current = toCodes(selected);
  return toCodes(saved)
    .filter((code) => !current.includes(code))
    .map((code) => ({
      country: code,
      count: getFeatureEnabledCount(
        (charityCountries ?? []).find(
          (row) => normaliseCountry(row.country) === code
        ),
        feature
      )
    }))
    .filter((removed) => removed.count > 0);
};
